import { useCallback } from "react";
import { useSelector } from "react-redux";

import { SHARE_SCOPE } from "@/constants";
import {
  currentExperienceSelector,
  currentPropertySelector,
  currentShareSelector,
  visualBuilderDataSelector,
} from "@/store/selectors";

import { useUrlParams } from "./useUrlParams";

type UtmParamValue = string | null;
type UtmParamKey =
  | "utm_campaign"
  | "utm_bucket"
  | "utm_share"
  | "utm_experience"
  | "utm_contact"
  | "utm_property"
  | "utm_media_item"
  | "utm_owner";
type UtmParams = Partial<Record<UtmParamKey, UtmParamValue>>;

type UtmOptions = {
  campaign?: string;
  contactId?: string;
};

const UTM_CAMPAIGNS = {
  EXPERIENCE_SHARE: "experience_share",
  SINGLE_ASSET_SHARE: "single_asset_share",
} as const;

const UTM_BUCKETS = {
  ASSET: "asset",
  EXPERIENCE: "experience",
  CHANNEL: "channel",
} as const;

const UTM_EXPERIENCE = {
  ALL_ASSET_SHARE: "all-asset-share",
} as const;

type UrlContext = {
  pathname: string;
  mediaItemId: string | number | null | undefined;
  experienceId: string | number | null | undefined;
  ownerId: string | number | null | undefined;
  shareScope: SHARE_SCOPE | null | undefined;
  isSingleExperience: boolean | null | undefined;
};

function createPresentationFullCollectionShareParams(context: UrlContext, baseParams: UtmParams): UtmParams {
  const params = { ...baseParams };
  params.utm_campaign = UTM_CAMPAIGNS.EXPERIENCE_SHARE;
  params.utm_bucket = [UTM_BUCKETS.ASSET, UTM_BUCKETS.EXPERIENCE, UTM_BUCKETS.CHANNEL].join(",");
  params.utm_experience = UTM_EXPERIENCE.ALL_ASSET_SHARE;
  return params;
}

function createFullExperienceParams(context: UrlContext, baseParams: UtmParams): UtmParams {
  const params = { ...baseParams };
  params.utm_campaign = UTM_CAMPAIGNS.EXPERIENCE_SHARE;
  params.utm_bucket = [UTM_BUCKETS.ASSET, UTM_BUCKETS.EXPERIENCE, UTM_BUCKETS.CHANNEL].join(",");
  params.utm_experience = context.experienceId?.toString() || null;
  params.utm_owner = context.ownerId?.toString() || null;
  return params;
}

function createSingleAssetShareParams(context: UrlContext, baseParams: UtmParams): UtmParams {
  const params = { ...baseParams };
  params.utm_campaign = UTM_CAMPAIGNS.SINGLE_ASSET_SHARE;
  params.utm_bucket = [UTM_BUCKETS.ASSET, UTM_BUCKETS.EXPERIENCE, UTM_BUCKETS.CHANNEL].join(",");
  params.utm_media_item = context.mediaItemId?.toString() || null;
  params.utm_experience = context.experienceId?.toString() || null;
  params.utm_owner = context.ownerId?.toString() || null;
  return params;
}

function createPresentationSingleAssetShareParams(context: UrlContext, baseParams: UtmParams): UtmParams {
  const params = { ...baseParams };
  params.utm_campaign = UTM_CAMPAIGNS.SINGLE_ASSET_SHARE;
  params.utm_bucket = [UTM_BUCKETS.ASSET, UTM_BUCKETS.EXPERIENCE, UTM_BUCKETS.CHANNEL].join(",");
  params.utm_experience = UTM_EXPERIENCE.ALL_ASSET_SHARE;
  params.utm_media_item = context.mediaItemId?.toString() || null;
  return params;
}

function createUtmParams(context: UrlContext, baseParams: UtmParams): UtmParams | undefined {
  const { shareScope } = context;

  if (shareScope === SHARE_SCOPE.PROPERTY) {
    return createPresentationFullCollectionShareParams(context, baseParams);
  }

  if (shareScope === SHARE_SCOPE.MEDIA_ITEM) {
    return createPresentationSingleAssetShareParams(context, baseParams);
  }

  if (context.experienceId && !context.isSingleExperience) {
    return createFullExperienceParams(context, baseParams);
  }

  if (context.experienceId && context.isSingleExperience) {
    return createSingleAssetShareParams(context, baseParams);
  }

  return undefined;
}

export const useUtmParams = () => {
  const params = useUrlParams();
  const { share } = useSelector(currentShareSelector);
  const { experience } = useSelector(currentExperienceSelector);
  const { property } = useSelector(currentPropertySelector);
  const { visualBuilderData } = useSelector(visualBuilderDataSelector(params));

  const propertyId = property.id;
  const shareId = share?.id;
  const experienceId = share?.experienceId;
  const ownerId = experience?.createdById;

  const shareScope = share?.options?.scope;
  const mediaItemId = visualBuilderData?.currentMediaItemId;
  const isSingleExperience = share?.options?.isSingleExperience;

  const createBaseUtmParams = useCallback((): UtmParams => {
    const params: UtmParams = {};

    if (propertyId) params.utm_property = propertyId.toString();
    if (shareId) params.utm_share = shareId.toString();

    return params;
  }, [propertyId, shareId]);

  const determineUtmParams = useCallback(
    (url: string, options: UtmOptions = {}): UtmParams | undefined => {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;

      const baseParams = createBaseUtmParams();

      const context: UrlContext = {
        pathname,
        mediaItemId,
        experienceId,
        ownerId,
        shareScope,
        isSingleExperience,
      };

      const params = createUtmParams(context, baseParams);

      if (!params) {
        return undefined;
      }

      if (options.campaign) {
        params.utm_campaign = options.campaign;
      }

      if (options.contactId) {
        params.utm_contact = options.contactId;
      }

      return params;
    },
    [createBaseUtmParams, mediaItemId, experienceId, ownerId, shareScope],
  );

  const augmentUrlWithUtmParams = useCallback(
    (url: string, options: UtmOptions = {}): string => {
      try {
        if (!url) {
          return url;
        }

        const urlObj = new URL(url);
        const params = determineUtmParams(url, options);

        if (!params) {
          return url;
        }

        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined) {
            urlObj.searchParams.set(key, value);
          }
        });

        return urlObj.toString();
      } catch (error) {
        console.error(`Error augmenting URL ${url} with UTM parameters:`, error);
        return url;
      }
    },
    [determineUtmParams],
  );

  const augmentMediaItemUrlWithUtmParams = useCallback(
    (url: string, mediaItemId: number): string => {
      try {
        if (!url) {
          return url;
        }

        const urlObj = new URL(url);

        urlObj.searchParams.set("utm_campaign", UTM_CAMPAIGNS.SINGLE_ASSET_SHARE);
        urlObj.searchParams.set("utm_bucket", [UTM_BUCKETS.ASSET, UTM_BUCKETS.CHANNEL].join(","));

        if (propertyId) {
          urlObj.searchParams.set("utm_property", propertyId.toString());
        }

        if (mediaItemId) {
          urlObj.searchParams.set("utm_media_item", mediaItemId.toString());
        }

        return urlObj.toString();
      } catch (error) {
        console.error(`Error augmenting media item URL ${url} with UTM parameters:`, error);
        return url;
      }
    },
    [propertyId],
  );

  return {
    augmentUrlWithUtmParams,
    augmentMediaItemUrlWithUtmParams,
  };
};
